import React from 'react';
import { motion } from 'motion/react';
import { Sparkles, Crown, Volume2 } from 'lucide-react';
import { soundManager } from '../lib/sound';

interface HeroShowcaseInteractiveProps {
  onQuickPlay?: () => void;
}

export const HeroShowcaseInteractive: React.FC<HeroShowcaseInteractiveProps> = ({ onQuickPlay }) => {
  return (
    <div className="relative w-full min-h-[400px] sm:min-h-[440px] md:h-[480px] rounded-3xl overflow-hidden shadow-[0_25px_60px_-15px_rgba(0,0,0,0.85),0_0_50px_rgba(212,175,55,0.2)] border-2 border-[#d4af37]/40 bg-gradient-to-b from-[#0a2315] via-[#06160d] to-[#020a05] select-none group flex flex-col justify-between">
      
      {/* 1. Ambient Lighting & Felt Texture */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-emerald-600/30 via-emerald-950/40 to-transparent pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,_var(--tw-gradient-stops))] from-amber-500/20 via-transparent to-transparent pointer-events-none" />
      <div className="absolute inset-0 opacity-20 bg-[radial-gradient(#d4af37_1px,transparent_1px)] [background-size:20px_20px] pointer-events-none" />

      {/* 2. Top Bar: VIP Club Badge & Live Voice Indicator */}
      <div className="relative z-20 pt-3 sm:pt-4 px-3 sm:px-6 flex items-center justify-between pointer-events-none">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-md border border-[#d4af37]/50 shadow-lg">
          <Crown className="w-3.5 h-3.5 text-[#fbbf24] animate-pulse" />
          <span className="text-[10px] sm:text-xs font-black uppercase tracking-[0.2em] text-[#fef3c7]">
            4 OYUNLU VIP MASASI
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* Live Voice Chat Pulsing Pill */}
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-cyan-950/80 backdrop-blur-md border border-cyan-500/40 text-[10px] sm:text-xs font-bold text-cyan-300 shadow">
            <Volume2 className="w-3.5 h-3.5 text-cyan-400 animate-bounce" />
            <span className="hidden xs:inline">CANLI SESLİ SOHBET</span>
            <span className="w-2 h-2 rounded-full bg-cyan-400 animate-ping" />
          </div>
        </div>
      </div>

      {/* 3. Main Stage: The 4 Quadrants Showcase */}
      <div className="relative w-full flex-1 grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 p-3 sm:p-5 items-center justify-center">
        
        {/* GAME 1: OKEY (Istaka & Okey Tiles) */}
        <motion.div 
          whileHover={{ scale: 1.04, y: -4 }}
          onClick={() => soundManager.play('tile_draw')}
          className="relative h-full min-h-[140px] sm:min-h-[160px] rounded-2xl bg-gradient-to-b from-[#3a1d0e]/90 via-[#241107]/90 to-[#140803]/90 border border-amber-500/40 p-2 sm:p-3 flex flex-col justify-between shadow-xl cursor-pointer group/card overflow-hidden"
        >
          <div className="absolute -right-4 -bottom-4 w-16 h-16 bg-amber-500/10 rounded-full blur-xl pointer-events-none" />
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span className="w-5 h-5 rounded-lg bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-xs">🀄</span>
              <span className="text-[11px] sm:text-xs font-black text-amber-200 tracking-wider">OKEY</span>
            </div>
            <span className="text-[8px] sm:text-[9px] font-bold text-amber-400/90 bg-amber-950/60 px-1.5 py-0.5 rounded border border-amber-500/30">4 KİŞİLİK</span>
          </div>

          {/* Okey Istaka Rack Mini View */}
          <div className="my-1.5 bg-[#170a04] p-1.5 rounded-xl border border-amber-900/60 flex items-center justify-center gap-1 shadow-inner">
            <div className="w-5 sm:w-6 h-8 sm:h-9 bg-gradient-to-b from-stone-50 to-stone-200 rounded shadow border border-stone-300 flex flex-col items-center justify-between py-0.5">
              <span className="text-[10px] sm:text-xs font-black text-red-600 leading-none">11</span>
              <span className="w-1 h-1 rounded-full bg-red-600" />
            </div>
            <div className="w-5 sm:w-6 h-8 sm:h-9 bg-gradient-to-b from-stone-50 to-stone-200 rounded shadow border border-stone-300 flex flex-col items-center justify-between py-0.5">
              <span className="text-[10px] sm:text-xs font-black text-red-600 leading-none">12</span>
              <span className="w-1 h-1 rounded-full bg-red-600" />
            </div>
            <div className="w-5 sm:w-6 h-8 sm:h-9 bg-gradient-to-b from-stone-50 to-stone-200 rounded shadow border border-stone-300 flex flex-col items-center justify-between py-0.5">
              <span className="text-[10px] sm:text-xs font-black text-red-600 leading-none">13</span>
              <span className="w-1 h-1 rounded-full bg-red-600" />
            </div>
            {/* Sahte Okey Highlighted */}
            <div className="w-5 sm:w-6 h-8 sm:h-9 bg-gradient-to-b from-amber-50 to-amber-100 rounded shadow border-2 border-amber-400 flex flex-col items-center justify-between py-0.5 animate-pulse">
              <span className="text-[8px] font-black text-amber-700">★</span>
              <span className="text-[9px] font-black text-amber-900 leading-none">OKEY</span>
            </div>
          </div>

          <div className="flex items-center justify-between text-[9px] text-amber-300/80 font-semibold">
            <span>Düz Masa Oyunu</span>
            <span className="text-amber-400 group-hover/card:translate-x-0.5 transition-transform">Oyna →</span>
          </div>
        </motion.div>

        {/* GAME 2: PİŞTİ (Jack on Jack Pişti with Double Score Badge) */}
        <motion.div 
          whileHover={{ scale: 1.04, y: -4 }}
          onClick={() => soundManager.play('deal')}
          className="relative h-full min-h-[140px] sm:min-h-[160px] rounded-2xl bg-gradient-to-b from-[#08202b]/90 via-[#04131b]/90 to-[#02090e]/90 border border-cyan-500/40 p-2 sm:p-3 flex flex-col justify-between shadow-xl cursor-pointer group/card overflow-hidden"
        >
          <div className="absolute -right-4 -bottom-4 w-16 h-16 bg-cyan-500/10 rounded-full blur-xl pointer-events-none" />

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span className="w-5 h-5 rounded-lg bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center text-xs">🃏</span>
              <span className="text-[11px] sm:text-xs font-black text-cyan-200 tracking-wider">PİŞTİ</span>
            </div>
            <span className="text-[8px] sm:text-[9px] font-bold text-cyan-400/90 bg-cyan-950/60 px-1.5 py-0.5 rounded border border-cyan-500/30">HIZLI OYUN</span>
          </div>

          {/* Pişti Card Pair View */}
          <div className="my-1.5 relative flex items-center justify-center h-12 sm:h-14">
            {/* Bottom Card */}
            <div className="absolute w-8 sm:w-10 h-11 sm:h-13 bg-white rounded-lg shadow-md border border-slate-300 p-1 transform -rotate-12 -translate-x-2 flex flex-col justify-between">
              <span className="text-[9px] sm:text-[10px] font-black text-slate-900 leading-none">J♣</span>
              <span className="text-center text-xs">♣</span>
            </div>
            {/* Top Card (Pişti Maker) */}
            <div className="absolute w-8 sm:w-10 h-11 sm:h-13 bg-white rounded-lg shadow-xl border-2 border-cyan-400 p-1 transform rotate-6 translate-x-2 flex flex-col justify-between z-10">
              <span className="text-[9px] sm:text-[10px] font-black text-slate-900 leading-none">J♠</span>
              <div className="text-center">
                <span className="text-[7px] font-black bg-cyan-500 text-slate-950 px-1 rounded uppercase">PİŞTİ!</span>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between text-[9px] text-cyan-300/80 font-semibold">
            <span>Vale & 10'lu Puanları</span>
            <span className="text-cyan-400 group-hover/card:translate-x-0.5 transition-transform">Oyna →</span>
          </div>
        </motion.div>

        {/* GAME 3: BATAK (İhaleli Koz Batak - Ace of Spades) */}
        <motion.div 
          whileHover={{ scale: 1.04, y: -4 }}
          onClick={() => soundManager.play('deal')}
          className="relative h-full min-h-[140px] sm:min-h-[160px] rounded-2xl bg-gradient-to-b from-[#2a0e2d]/90 via-[#18071a]/90 to-[#0c030e]/90 border border-fuchsia-500/40 p-2 sm:p-3 flex flex-col justify-between shadow-xl cursor-pointer group/card overflow-hidden"
        >
          <div className="absolute -right-4 -bottom-4 w-16 h-16 bg-fuchsia-500/10 rounded-full blur-xl pointer-events-none" />

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span className="w-5 h-5 rounded-lg bg-fuchsia-500/20 border border-fuchsia-500/40 flex items-center justify-center text-xs">♠️</span>
              <span className="text-[11px] sm:text-xs font-black text-fuchsia-200 tracking-wider">BATAK</span>
            </div>
            <span className="text-[8px] sm:text-[9px] font-bold text-fuchsia-400/90 bg-fuchsia-950/60 px-1.5 py-0.5 rounded border border-fuchsia-500/30">İHALELİ & KOZ</span>
          </div>

          {/* Batak Ace of Spades & Koz Badge */}
          <div className="my-1.5 flex items-center justify-center gap-1.5">
            <div className="w-9 sm:w-11 h-12 sm:h-14 bg-gradient-to-b from-white to-fuchsia-50 rounded-lg shadow-lg border-2 border-fuchsia-400 p-1 flex flex-col justify-between">
              <div className="flex justify-between items-center leading-none">
                <span className="text-[10px] sm:text-xs font-black text-slate-950">A</span>
                <span className="text-[9px] text-slate-900">♠</span>
              </div>
              <div className="text-center text-base sm:text-lg text-slate-950 font-serif">♠</div>
              <div className="text-[7px] font-black text-fuchsia-700 uppercase text-center">KOZ AS</div>
            </div>
            <div className="flex flex-col gap-1 text-[8px] font-bold text-fuchsia-300">
              <span className="bg-fuchsia-950/80 px-1.5 py-0.5 rounded border border-fuchsia-500/30">İhale: 8</span>
              <span className="bg-fuchsia-950/80 px-1.5 py-0.5 rounded border border-fuchsia-500/30">Koz: Maça</span>
            </div>
          </div>

          <div className="flex items-center justify-between text-[9px] text-fuchsia-300/80 font-semibold">
            <span>Stratejik İhale Oyunu</span>
            <span className="text-fuchsia-400 group-hover/card:translate-x-0.5 transition-transform">Oyna →</span>
          </div>
        </motion.div>

        {/* GAME 4: KING (Rıfkı, Ceza & Koz Turlu) */}
        <motion.div 
          whileHover={{ scale: 1.04, y: -4 }}
          onClick={() => soundManager.play('deal')}
          className="relative h-full min-h-[140px] sm:min-h-[160px] rounded-2xl bg-gradient-to-b from-[#2b1f09]/90 via-[#1a1204]/90 to-[#0d0901]/90 border border-yellow-500/40 p-2 sm:p-3 flex flex-col justify-between shadow-xl cursor-pointer group/card overflow-hidden"
        >
          <div className="absolute -right-4 -bottom-4 w-16 h-16 bg-yellow-500/10 rounded-full blur-xl pointer-events-none" />

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span className="w-5 h-5 rounded-lg bg-yellow-500/20 border border-yellow-500/40 flex items-center justify-center text-xs">👑</span>
              <span className="text-[11px] sm:text-xs font-black text-yellow-200 tracking-wider">KİNG</span>
            </div>
            <span className="text-[8px] sm:text-[9px] font-bold text-yellow-400/90 bg-yellow-950/60 px-1.5 py-0.5 rounded border border-yellow-500/30">RIFKI & KOZ</span>
          </div>

          {/* King of Hearts (Rıfkı) View */}
          <div className="my-1.5 flex items-center justify-center gap-1.5">
            <div className="w-9 sm:w-11 h-12 sm:h-14 bg-gradient-to-b from-white to-red-50 rounded-lg shadow-lg border-2 border-yellow-400 p-1 flex flex-col justify-between">
              <div className="flex justify-between items-center leading-none">
                <span className="text-[10px] sm:text-xs font-black text-red-600">K</span>
                <span className="text-[9px] text-red-600">♥</span>
              </div>
              <div className="text-center text-base sm:text-lg text-red-600 font-serif">♥</div>
              <div className="text-[7px] font-black text-yellow-800 bg-yellow-200/90 rounded px-0.5 text-center">RIFKI</div>
            </div>
            <div className="flex flex-col gap-1 text-[8px] font-bold text-yellow-300">
              <span className="bg-yellow-950/80 px-1.5 py-0.5 rounded border border-yellow-500/30">Ceza: -320</span>
              <span className="bg-yellow-950/80 px-1.5 py-0.5 rounded border border-yellow-500/30">Koz: +50</span>
            </div>
          </div>

          <div className="flex items-center justify-between text-[9px] text-yellow-300/80 font-semibold">
            <span>Usta İskambil Ligi</span>
            <span className="text-yellow-400 group-hover/card:translate-x-0.5 transition-transform">Oyna →</span>
          </div>
        </motion.div>
      </div>

      {/* 4. Bottom Interactive Footer Bar */}
      <div className="relative z-20 mb-2.5 sm:mb-3 mx-2.5 sm:mx-4 flex items-center justify-between px-3 sm:px-4 py-2 rounded-2xl bg-black/75 backdrop-blur-md border border-[#d4af37]/40 gap-2">
        <div className="flex items-center gap-2 text-amber-200/90 text-[11px] sm:text-xs font-semibold">
          <Sparkles className="w-4 h-4 text-[#fbbf24] shrink-0" />
          <span className="text-[#fef3c7] font-bold">Pişti • Batak • King • Okey</span>
          <span className="hidden md:inline text-amber-300/70 font-normal">| Tek tıkla hemen masaya oturun</span>
        </div>

        <button
          onClick={onQuickPlay}
          className="px-3.5 sm:px-5 py-1.5 rounded-xl bg-gradient-to-r from-[#d4af37] via-[#f59e0b] to-[#d4af37] hover:from-[#f59e0b] hover:to-[#d4af37] text-slate-950 font-black text-[10px] sm:text-xs uppercase tracking-wider shadow-[0_0_15px_rgba(245,158,11,0.5)] transition-all transform active:scale-95 cursor-pointer shrink-0"
        >
          Hemen Masaya Geç
        </button>
      </div>
    </div>
  );
};
